<template>
	<v-form
		class="login-form"
		ref="form"
		v-model="valid"
		lazy-validation
		@submit.prevent="validateAndSubmit"
	>
		<v-row>
			<v-col cols="12">
				<v-text-field
					class="pt-0 email-field"
					color="red"
					v-model="fields.login"
					:error-messages="field_errors.email"
					:rules="rules.email"
					required
					:placeholder="placeHolderLogin"
				>
				</v-text-field>
			</v-col>
			<v-col cols="12" class="pb-0">
				<v-text-field
					class="pt-0 password-field"
					:append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
					:type="showPassword ? 'text' : 'password'"
					v-model="fields.password"
					required
					:rules="rules.password"
					:error-messages="field_errors.password"
					:placeholder="placeHolderPassword"
					@click:append="showPassword = !showPassword"
				>
				</v-text-field>
				<div class="d-flex">
					<CustomCheckbox
						:colorBackground="colorBackground"
						:colorTick="colorTick"
						:isChecked="isChecked"
						:onChange="toggleIsChecked"
						value="remember-me"
					/>
					<label class="login-form__remember-me">Remember me</label>
				</div>
			</v-col>
			<p
				class="text-center red--text"
				v-for="error in errorList"
				:key="error"
				style="width: 100%"
			>
				{{ error }}
			</p>
			<v-col cols="12" class="text-center mb-4">
				<v-btn
					class="align-center px-11 py-6 login-form__submit"
					type="submit"
					:loading="isLoading"
				>
					{{ loginButtonName }}
				</v-btn>
				<p class="terms-of-use" v-html="policyAndTermText"></p>
			</v-col>
			<v-col cols="12">
				<v-row
					v-if="isForgotPasswordEnable && forgotPasswordUrl"
					no-gutters
					class="justify-center"
				>
					<template v-if="externalUrl == 1">
						<a :href="forgotPasswordUrl" target="_blank" class="login-form__link">
							{{ forgotPasswordTitle }}
						</a>
					</template>
					<template v-else>
						<router-link :to="{ name: 'restore-password' }" class="login-form__link">
							{{ forgotPasswordTitle }}
						</router-link>
					</template>
				</v-row>
			</v-col>
		</v-row>
	</v-form>
</template>

<script>
import Form from '../../mixins/Form'
import CustomCheckbox from '@/components/CustomCheckbox.vue'
import { mapActions, mapGetters } from 'vuex'
import { rulePassword, ruleRequired } from '@/utils/rules'
import LocalStorageService from '@/services/local-storage.service'

export default {
	name: 'LoginForm',
	mixins: [Form],
	components: {
		CustomCheckbox,
	},
	data: () => ({
		colorBackground: 'var(--button-login-background)',
		colorTick: 'var(--button-login)',
		fields: {
			login: '',
			password: '',
			slug: '',
			user_category_id: '',
		},
		isChecked: false,
		showPassword: false,
	}),
	props: {
		categorySettings: {
			type: Object,
			default: null,
		},
		additionalLoginSettings: {
			type: Object,
			default: null,
		},
	},
	computed: {
		...mapGetters('auth', ['authErrors', 'isLoading']),
		...mapGetters('settings', ['login', 'baseCore', 'styles']),

		rules() {
			return {
				email: [(v) => ruleRequired(v) || 'Field is required.'],
				password: [
					(v) => ruleRequired(v) || 'Field is required.',
					(v) => rulePassword(v) || 'Password must contain a minimum of 5 characters.',
				],
			}
		},
		errorList() {
			if (this.authErrors.length <= 0) {
				return []
			}
			return [...new Set(this.authErrors)].filter((item) => item)
		},
		loginButtonName() {
			const { login_button_name: loginCategoryButton } = this.categorySettings || {}
			return loginCategoryButton || 'Log in'
		},
		placeHolderLogin() {
			const { login_field_title: loginCategoryPlaceHolder } = this.categorySettings || {}
			return loginCategoryPlaceHolder || 'Username / Email'
		},
		placeHolderPassword() {
			const { password_field_title: passwordCategoryPlaceHolder } =
				this.categorySettings || {}
			return passwordCategoryPlaceHolder || 'Password'
		},
		isForgotPasswordEnable() {
			const { forgot_password_enabled: forgotPasswordCategoryEnabled } =
				this.categorySettings || {}
			return forgotPasswordCategoryEnabled
		},
		externalUrl() {
			const { external_url: externalCategoryUrl } = this.categorySettings || {}
			return externalCategoryUrl
		},
		forgotPasswordUrl() {
			const { forgot_password_url: forgotPasswordCategoryUrl } = this.categorySettings || {}
			return forgotPasswordCategoryUrl
		},
		forgotPasswordTitle() {
			const { forgot_password_title: forgotPasswordCategoryTitle } =
				this.categorySettings || {}
			return forgotPasswordCategoryTitle || 'Forgot Password?'
		},
		policyAndTermText() {
			const { license_text: licenseText } = this.categorySettings || {}
			const { login_button_name: loginButtonName } = this.categorySettings || {}
			const { privacy_policy: policy, terms_of_use: termOfUse } =
				this.additionalLoginSettings || {}

			return licenseText
				.replace(/{%login.login_button_name%}/g, loginButtonName)
				.replace(/{%core.privacy_policy%}/g, policy)
				.replace(/{%core.terms_of_use%}/g, termOfUse)
				.replace(/href/g, 'target="_blank" href')
		},
	},
	methods: {
		...mapActions('auth', {
			loginHandle: 'login',
			signInWithGoogle: 'signInWithGoogle',
			signInWithFacebook: 'signInWithFacebook',
			signInWithApple: 'signInWithApple',
		}),
		submitForm() {
			return this.loginHandle(this.fields).then((response) => {
				const {
					renew_url,
					renew_button_name,
					renew_area_text,
					privacy_policy,
					terms_of_use,
				} = this.additionalLoginSettings || {}

				const payload = {
					renew_url: renew_url || '',
					renew_button_name: renew_button_name || '',
					renew_area_text: renew_area_text || '',
					privacy_policy: privacy_policy || '',
					terms_of_use: terms_of_use || '',
				}
				LocalStorageService.set('additionalLoginSettings', JSON.stringify(payload))

				return response
			})
		},
		handleRenewButtonClick() {
			const { renew_url: renewUrl } = this.additionalLoginSettings || {}
			if (renewUrl) {
			}
		},
		navigateToRegisterPage() {
			this.$router.push({ name: 'register' })
		},
		toggleIsChecked() {
			this.isChecked = !this.isChecked
		},
	},
	created() {
		const userCategoryId = this.$route.query.user_category_id
		if (userCategoryId) {
			this.fields.user_category_id = userCategoryId
		}
		const slug = this.$route.query.slug
		if (slug) {
			this.fields.slug = slug
		}
	},
}
</script>

<style lang="scss">
.login-form {
	font-family: Montserrat, system-ui;

	.remember-me {
		.v-label {
			font-style: normal;
			font-weight: 500;
			font-size: 15px;
		}
	}

	.theme--light.v-input input {
		color: #000;
	}

	.password-field,
	.email-field {
		input {
			font-size: 13px;
			font-weight: 500;
			max-height: 50px;
			padding-left: 8px;
		}

		.v-input__slot {
			border-bottom: 1px solid rgba(0, 0, 0, 0.6);
		}
	}

	.theme--light.v-text-field > .v-input__control > .v-input__slot:before {
		color: #e5e5e5;
		border-color: #e5e5e5;
	}

	.password-field {
		.v-input__append-inner {
			margin-top: 0;

			.v-input__icon--append {
				.v-icon {
					color: #e5e5e5;
					padding: 30px 0 20px;
				}
			}
		}
	}

	input::placeholder {
		color: #e5e5e5 !important;
		opacity: 1;
	}

	&__description {
		font-weight: bold;
		font-size: 18px;
	}

	&__link {
		font-style: normal;
		font-weight: 500;
		font-size: 14px;
		color: var(--text_button_tint) !important;
		text-decoration: underline;
	}

	&__social {
		p {
			font-weight: 500;
			font-size: 14px;
			line-height: 17px;
		}

		button {
			width: 61px;
			height: 61px !important;
			box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
			border-radius: 12px;
		}
	}

	button[type='submit'] {
		border-radius: 8px;
		font-style: normal;
		font-weight: bold;
		font-size: 16px;
		text-transform: none;
		background-color: var(--button_main_background) !important;
		color: var(--button_main_text_color) !important;
		max-width: 420px;
		width: 100%;
	}

	.terms-of-use {
		padding-top: 20px;
		max-width: 420px;
		width: 100%;
		color: #a0a0a0 !important;
		font-size: 12px;
		font-family: Montserrat, sans-serif;
		font-weight: 500;
		text-align: left;

		a {
			color: var(--link) !important;
		}
	}
}
</style>
